import React, { useState, useEffect, useCallback, useMemo } from 'react';
import apiClient from '../../utils/apiClient';
import server from '../../utils/serverConfig';
import {
    RefreshCw, Loader2, AlertCircle, TrendingUp
} from 'lucide-react';
import BenchmarkToolbar from './components/BenchmarkToolbar';
import EquityChartContainer from './components/EquityChartContainer';
import SidebarSeriesChecklist from './components/SidebarSeriesChecklist';
import PerformanceRankingsTable from './components/PerformanceRankingsTable';
import MultiGridFullscreenModal from './components/MultiGridFullscreenModal';

const AdaptiveDashboardView = () => {
    const [startDate, setStartDate] = useState('2021-01-01');
    const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const applyYearPreset = (years) => {
        const end = endDate ? new Date(endDate) : new Date();
        if (isNaN(end.getTime())) return;
        
        if (years === 'MAX') {
            setStartDate('2021-01-01');
        } else {
            const start = new Date(end);
            start.setFullYear(start.getFullYear() - years);
            setStartDate(start.toISOString().split('T')[0]);
        }
    };

    // Filters selection states
    const [selectedIndex, setSelectedIndex] = useState('NIFTY');
    const [selectedOptionType, setSelectedOptionType] = useState('All');
    const [activeRegimes, setActiveRegimes] = useState({});
    const [activeRegimesLoading, setActiveRegimesLoading] = useState(false);

    // Sub-tab selection state: 'matrix' | 'equity_curve'
    const [dashboardSubTab, setDashboardSubTab] = useState('matrix');

    // Equity Curve states
    const [equityTrades, setEquityTrades] = useState([]);
    const [equityLoading, setEquityLoading] = useState(false);
    const [equityError, setEquityError] = useState(null);
    const [groupingMode, setGroupingMode] = useState('strategy'); // 'strategy' | 'regime' | 'asset' | 'asset_strategy' | 'asset_regime' | 'asset_strategy_regime' | 'portfolio'
    const [selectedSeries, setSelectedSeries] = useState({});
    const [pnlUnit, setPnlUnit] = useState('inr'); // 'inr' | 'pct'
    const [chartView, setChartView] = useState('cumulative'); // 'cumulative' | 'daily'
    const [sidebarSort, setSidebarSort] = useState('pnl'); // 'alphabetical' | 'pnl'
    const [sidebarFilterMode, setSidebarFilterMode] = useState('all'); // 'all' | 'active_regimes' | 'live'
    const [highlightLive, setHighlightLive] = useState(false);
    const [hoveredSeries, setHoveredSeries] = useState(null);
    const [pinnedSeries, setPinnedSeries] = useState(null);
    const [cumulativeSubMode, setCumulativeSubMode] = useState('benchmark');
    const [isMultiGridFullscreen, setIsMultiGridFullscreen] = useState(false); // 'benchmark' | 'split' | 'multi' | 'series'
    const [multiGridCount, setMultiGridCount] = useState(6); // 2 | 4 | 6
    const [multiPanels, setMultiPanels] = useState(() => [
        { id: 0, strategy: 'ALL', selectedFSR: ['R', 'S', 'F'], selectedMABits: ['00', '01', '10', '11'], selectedCEPE: ['CE', 'PE'] },
        { id: 1, strategy: 'ALL', selectedFSR: ['R', 'S', 'F'], selectedMABits: ['00', '01', '10', '11'], selectedCEPE: ['CE', 'PE'] },
        { id: 2, strategy: 'ALL', selectedFSR: ['R', 'S', 'F'], selectedMABits: ['00', '01', '10', '11'], selectedCEPE: ['CE', 'PE'] },
        { id: 3, strategy: 'ALL', selectedFSR: ['R', 'S', 'F'], selectedMABits: ['00', '01', '10', '11'], selectedCEPE: ['CE', 'PE'] },
        { id: 4, strategy: 'ALL', selectedFSR: ['R', 'S', 'F'], selectedMABits: ['00', '01', '10', '11'], selectedCEPE: ['CE', 'PE'] },
        { id: 5, strategy: 'ALL', selectedFSR: ['R', 'S', 'F'], selectedMABits: ['00', '01', '10', '11'], selectedCEPE: ['CE', 'PE'] },
    ]);
    const [showWholeBaseline, setShowWholeBaseline] = useState(true);
    const [showLiveGridFiltered, setShowLiveGridFiltered] = useState(false);
    const [selectedFSR, setSelectedFSR] = useState(['R', 'S', 'F']);
    const [selectedMABits, setSelectedMABits] = useState(['00', '01', '10', '11']);
    const [selectedCEPE, setSelectedCEPE] = useState(['CE', 'PE']);
    const [baselineScope, setBaselineScope] = useState('selected'); // 'selected' | 'all'
    const [filterOnBaseline, setFilterOnBaseline] = useState(false); // default: false (unfiltered baseline)

    // Trade drill-down states
    const [selectedCell, setSelectedCell] = useState(null); // { strategy, regime, type }
    const [trades, setTrades] = useState([]);
    const [tradesLoading, setTradesLoading] = useState(false);
    const [tradesError, setTradesError] = useState(null);

    // Rankings states
    const [showRankings, setShowRankings] = useState(false);
    const [rankingsTab, setRankingTab] = useState('strategies'); // 'strategies' | 'regimes' | 'combinations'
    const [rankingsData, setRankingsData] = useState(null);
    const [rankingsLoading, setRankingsLoading] = useState(false);
    const [rankingsError, setRankingsError] = useState(null);

    const getLotSize = (indexName, ticker) => {
        const idx = (indexName || '').toUpperCase();
        const tick = (ticker || '').toUpperCase();
        if (idx === 'NIFTY' || tick.includes('NIFTY')) return 65;
        if (idx === 'BANKNIFTY' || tick.includes('BANKNIFTY')) return 30;
        if (idx === 'FINNIFTY' || tick.includes('FINNIFTY')) return 60;
        if (idx === 'MIDCPNIFTY' || idx === 'MIDCAP' || tick.includes('MIDCPNIFTY') || tick.includes('MIDCAP')) return 120;
        if (idx === 'SENSEX' || tick.includes('SENSEX')) return 20;
        return 1;
    };

    const fetchRankings = async () => {
        setShowRankings(true);
        setRankingsLoading(true);
        setRankingsError(null);
        try {
            const url = server.godseye.baseUrl + 'audit-results/rankings?start_date=' + encodeURIComponent(startDate) + '&end_date=' + encodeURIComponent(endDate) + '&index_name=' + encodeURIComponent(selectedIndex) + '&ce_pe=' + encodeURIComponent(selectedOptionType);
            const res = await apiClient.get(url);
            setRankingsData(res.data);
        } catch (e) {
            console.error("Failed to fetch rankings", e);
            setRankingsError("Failed to load rankings.");
        } finally {
            setRankingsLoading(false);
        }
    };

    const fetchActiveRegimes = useCallback(async () => {
        setActiveRegimesLoading(true);
        try {
            const idxToFetch = selectedIndex === 'ALL' ? 'NIFTY' : selectedIndex;
            const url = server.godseye.baseUrl + 'audit-results/active-regimes?index_name=' + encodeURIComponent(idxToFetch);
            const res = await apiClient.get(url);
            if (res.data) {
                setActiveRegimes(res.data);
            }
        } catch (e) {
            console.error('Failed to fetch active regimes mapping', e);
        } finally {
            setActiveRegimesLoading(false);
        }
    }, [selectedIndex]);

    const fetchStats = useCallback(async () => {
        if (!startDate || !endDate) return;
        setLoading(true);
        setError(null);
        try {
            const url = server.godseye.baseUrl + 'audit-results/stats?start_date=' + encodeURIComponent(startDate) + '&end_date=' + encodeURIComponent(endDate) + '&index_name=' + encodeURIComponent(selectedIndex) + '&ce_pe=' + encodeURIComponent(selectedOptionType);
            const res = await apiClient.get(url);
            if (res.data) setStats(res.data);
        } catch (e) {
            console.error('Failed to fetch audit stats', e);
            setError('Failed to fetch performance stats.');
        } finally {
            setLoading(false);
        }
    }, [startDate, endDate, selectedIndex, selectedOptionType]);

    const fetchEquityTrades = useCallback(async () => {
        if (!startDate || !endDate) return;
        setEquityLoading(true);
        setEquityError(null);
        try {
            const params = new URLSearchParams({
                start_date: startDate,
                end_date: endDate,
                limit: 50000,
            });
            if (selectedIndex && selectedIndex.toUpperCase() !== 'ALL') {
                params.append('index_name', selectedIndex);
            }
            const url = server.godseye.baseUrl + 'audit-results/raw-backtest?' + params.toString();
            const res = await apiClient.get(url);
            setEquityTrades(res.data || []);
        } catch (e) {
            console.error('Failed to fetch equity trades', e);
            setEquityError('Failed to load equity trades.');
        } finally {
            setEquityLoading(false);
        }
    }, [startDate, endDate, selectedIndex]);

    useEffect(() => {
        fetchStats();
    }, [fetchStats]);

    useEffect(() => {
        fetchActiveRegimes();
    }, [fetchActiveRegimes]);

    useEffect(() => {
        if (dashboardSubTab === 'equity_curve') {
            fetchEquityTrades();
        }
    }, [dashboardSubTab, fetchEquityTrades]);

    const checkIsLiveGrid = useCallback((seriesName) => {
        if (!activeRegimes || Object.keys(activeRegimes).length === 0) return false;
        const getSeriesRegime = () => {
            if (groupingMode === 'regime') return seriesName;
            if (groupingMode === 'asset_regime') return seriesName.split(' - ')[1] || '';
            if (groupingMode === 'asset_strategy_regime') return seriesName.split(' - ')[2] || '';
            return '';
        };
        const getSeriesStrategy = () => {
            if (groupingMode === 'strategy') return seriesName;
            if (groupingMode === 'asset_strategy') return seriesName.split(' - ')[1] || '';
            if (groupingMode === 'asset_strategy_regime') return seriesName.split(' - ')[1] || '';
            return '';
        };
        const strategy = getSeriesStrategy();
        const regime = getSeriesRegime();

        const isStratActiveInGrid = (stratName) => {
            const config = activeRegimes[stratName];
            if (!config) return false;
            if (selectedOptionType === 'All') return (config['CE']?.length > 0 || config['PE']?.length > 0);
            return (config[selectedOptionType.toUpperCase()]?.length > 0);
        };
        const isRegimeActiveInGrid = (regName) => {
            const regUpper = regName.toUpperCase();
            return Object.values(activeRegimes).some(config =>
                (config['CE'] || []).includes(regUpper) || (config['PE'] || []).includes(regUpper)
            );
        };
        const isComboLiveInGrid = (stratName, regName) => {
            const config = activeRegimes[stratName];
            if (!config) return false;
            const regUpper = regName.toUpperCase();
            if (selectedOptionType === 'All') return (config['CE'] || []).includes(regUpper) || (config['PE'] || []).includes(regUpper);
            return (config[selectedOptionType.toUpperCase()] || []).includes(regUpper);
        };

        if (groupingMode === 'strategy' || groupingMode === 'asset_strategy') return isStratActiveInGrid(strategy);
        if (groupingMode === 'regime' || groupingMode === 'asset_regime') return isRegimeActiveInGrid(regime);
        if (groupingMode === 'asset_strategy_regime') return isComboLiveInGrid(strategy, regime);
        return false;
    }, [activeRegimes, groupingMode, selectedOptionType]);

    // Equity Curve Data Processing
    const equityChartData = useMemo(() => {
        if (equityTrades.length === 0) return { data: [], seriesList: [] };

        const grouped = {};
        equityTrades.forEach(trade => {
            let key = '';
            if (groupingMode === 'strategy') {
                key = trade.strategy || 'Unknown';
            } else if (groupingMode === 'regime') {
                key = trade.regime_code || 'Unknown';
            } else if (groupingMode === 'asset') {
                key = trade.index_name || 'Unknown';
            } else if (groupingMode === 'asset_strategy') {
                key = (trade.index_name || 'Unknown') + ' - ' + (trade.strategy || 'Unknown');
            } else if (groupingMode === 'asset_regime') {
                key = (trade.index_name || 'Unknown') + ' - ' + (trade.regime_code || 'Unknown');
            } else if (groupingMode === 'asset_strategy_regime') {
                key = (trade.index_name || 'Unknown') + ' - ' + (trade.strategy || 'Unknown') + ' - ' + (trade.regime_code || 'Unknown');
            } else {
                key = 'Portfolio';
            }

            if (!grouped[key]) grouped[key] = [];
            grouped[key].push(trade);
        });

        const seriesList = Object.keys(grouped).sort();

        const dateGrouped = {};
        const wholeDailyPnL = {};
        const filteredDailyPnL = {};
        let wholeTradeCount = 0;
        let filteredTradeCount = 0;
        const allDates = new Set();

        const hasSelectionKeys = Object.keys(selectedSeries).some(k => selectedSeries[k]);

        Object.entries(grouped).forEach(([series, trades]) => {
            const isSeriesSelected = hasSelectionKeys ? Boolean(selectedSeries[series]) : true;

            trades.forEach(t => {
                const dateStr = (t.sell_date || t.buy_date || '').split('T')[0];
                if (!dateStr) return;

                allDates.add(dateStr);
                if (!dateGrouped[dateStr]) dateGrouped[dateStr] = {};
                const lotSize = getLotSize(t.index_name, t.ticker);
                const pnlVal = pnlUnit === 'inr' ? Number(t.pnl || 0) * lotSize : Number(t.pnl_pct || 0) * 100;

                // Compute regime and option type info for filtering
                const regCode = (t.regime_code || '').trim();
                const fsr = regCode[0];
                const maBits = regCode.slice(1, 3);
                const cePe = (t.ce_pe || t.option_type || '').toUpperCase();

                const passFSR = selectedFSR.length === 0 || !fsr || selectedFSR.includes(fsr);
                const passMABits = selectedMABits.length === 0 || !maBits || selectedMABits.includes(maBits);
                const passCEPE = selectedCEPE.length === 0 || !cePe || selectedCEPE.includes(cePe);

                // Live Grid Check for trade strategy + CE/PE + regime code
                const strat = t.strategy;
                const regUpper = regCode.toUpperCase();
                const isTradeLive = Boolean(
                    activeRegimes &&
                    strat &&
                    activeRegimes[strat] &&
                    (activeRegimes[strat][cePe] || []).includes(regUpper)
                );

                // Individual series lines: apply Live Regime Grid filter AND FSR/MA bits/CE-PE UI filters
                if (isTradeLive && passFSR && passMABits && passCEPE) {
                    dateGrouped[dateStr][series] = (dateGrouped[dateStr][series] || 0) + pnlVal;
                }

                // Baseline scoping: 'all' includes all strategies in portfolio; 'selected' includes only checked series
                const includeInBaseline = baselineScope === 'all' ? true : isSeriesSelected;

                // Benchmark lines: whole baseline and live grid filtered
                if (includeInBaseline) {
                    // 1. Whole Tradelist Baseline: if filterOnBaseline is true, apply FSR, MA bits, and CE/PE filters
                    if (!filterOnBaseline || (passFSR && passMABits && passCEPE)) {
                        wholeDailyPnL[dateStr] = (wholeDailyPnL[dateStr] || 0) + pnlVal;
                        wholeTradeCount++;
                    }

                    // 2. Live Grid Filtered: only trades where regime is active in Live Regime Grid
                    if (isTradeLive) {
                        filteredDailyPnL[dateStr] = (filteredDailyPnL[dateStr] || 0) + pnlVal;
                        filteredTradeCount++;
                    }
                }
            });
        });


        const sortedDates = Array.from(allDates).sort();

        const chartRows = [];
        const runningTotals = {};
        let runningWhole = 0;
        let runningFiltered = 0;

        seriesList.forEach(s => {
            runningTotals[s] = 0;
        });

        if (sortedDates.length > 0) {
            const firstDate = new Date(sortedDates[0]);
            firstDate.setDate(firstDate.getDate() - 1);
            const startRow = { date: firstDate.toISOString().split('T')[0], 'Whole Tradelist': 0, 'Live Grid Filtered': 0 };
            seriesList.forEach(s => {
                startRow[s] = 0;
            });
            chartRows.push(startRow);
        }

        sortedDates.forEach(date => {
            const row = { date };
            const wPnl = wholeDailyPnL[date] || 0;
            const fPnl = filteredDailyPnL[date] || 0;

            runningWhole += wPnl;
            runningFiltered += fPnl;

            row['Whole Tradelist'] = Math.round(runningWhole * 100) / 100;
            row['Live Grid Filtered'] = Math.round(runningFiltered * 100) / 100;

            seriesList.forEach(s => {
                const pnl = (dateGrouped[date] && dateGrouped[date][s]) || 0;
                if (chartView === 'daily') {
                    row[s] = Math.round(pnl * 100) / 100;
                } else {
                    runningTotals[s] += pnl;
                    row[s] = Math.round(runningTotals[s] * 100) / 100;
                }
            });
            chartRows.push(row);
        });

        const wholeEndEquity = runningWhole;
        const filteredEndEquity = runningFiltered;
        const gridAlpha = filteredEndEquity - wholeEndEquity;

        return {
            data: chartRows,
            seriesList,
            wholeTradeCount,
            filteredTradeCount,
            wholeEndEquity,
            filteredEndEquity,
            gridAlpha
        };
    }, [equityTrades, groupingMode, pnlUnit, chartView, checkIsLiveGrid, selectedFSR, selectedMABits, selectedCEPE, baselineScope, filterOnBaseline, activeRegimes, selectedSeries]);

    const multiGridChartData = useMemo(() => {
        if (cumulativeSubMode !== 'multi') return [];

        return multiPanels.slice(0, multiGridCount).map((panel, idx) => {
            let targetStrategy = panel.strategy || (equityChartData.seriesList && equityChartData.seriesList[idx % equityChartData.seriesList.length]) || 'ALL';

            const panelFSR = panel.selectedFSR || ['R', 'S', 'F'];
            const panelMABits = panel.selectedMABits || ['00', '01', '10', '11'];
            const panelCEPE = panel.selectedCEPE || ['CE', 'PE'];

            const dateGrouped = {};
            const wholeDailyPnL = {};
            const filteredDailyPnL = {};
            let wholeTradeCount = 0;
            let filteredTradeCount = 0;
            const allDates = new Set();

            equityTrades.forEach(t => {
                const dateStr = (t.sell_date || t.buy_date || '').split('T')[0];
                if (!dateStr) return;

                let seriesKey = '';
                if (groupingMode === 'strategy') seriesKey = t.strategy || 'Unknown';
                else if (groupingMode === 'regime') seriesKey = t.regime_code || 'Unknown';
                else if (groupingMode === 'asset') seriesKey = t.index_name || 'Unknown';
                else if (groupingMode === 'asset_strategy') seriesKey = (t.index_name || 'Unknown') + ' - ' + (t.strategy || 'Unknown');
                else seriesKey = t.strategy || 'Unknown';

                const isTradeInTargetStrat = (targetStrategy === 'ALL' || seriesKey === targetStrategy || t.strategy === targetStrategy);

                const lotSize = getLotSize(t.index_name, t.ticker);
                const pnlVal = pnlUnit === 'inr' ? Number(t.pnl || 0) * lotSize : Number(t.pnl_pct || 0) * 100;

                const regCode = (t.regime_code || '').trim();
                const fsr = regCode[0];
                const maBits = regCode.slice(1, 3);
                const cePe = (t.ce_pe || t.option_type || '').toUpperCase();

                const passFSR = panelFSR.length === 0 || !fsr || panelFSR.includes(fsr);
                const passMABits = panelMABits.length === 0 || !maBits || panelMABits.includes(maBits);
                const passCEPE = panelCEPE.length === 0 || !cePe || panelCEPE.includes(cePe);

                const strat = t.strategy;
                const regUpper = regCode.toUpperCase();
                const isTradeLive = Boolean(
                    activeRegimes &&
                    strat &&
                    activeRegimes[strat] &&
                    (activeRegimes[strat][cePe] || []).includes(regUpper)
                );

                const pFilterOnBaseline = panel.filterOnBaseline !== undefined ? panel.filterOnBaseline : filterOnBaseline;
                const passGranularFilters = passFSR && passMABits && passCEPE;

                const isWholeBaselineTrade = (baselineScope === 'all' || isTradeInTargetStrat);

                if (isWholeBaselineTrade) {
                    if (!pFilterOnBaseline || passGranularFilters) {
                        allDates.add(dateStr);
                        wholeDailyPnL[dateStr] = (wholeDailyPnL[dateStr] || 0) + pnlVal;
                        wholeTradeCount++;
                    }
                }

                if (isTradeInTargetStrat && isTradeLive && passGranularFilters) {
                    allDates.add(dateStr);
                    filteredDailyPnL[dateStr] = (filteredDailyPnL[dateStr] || 0) + pnlVal;
                    filteredTradeCount++;
                }
            });

            const sortedDates = Array.from(allDates).sort();
            const rows = [];
            let rWhole = 0;
            let rFiltered = 0;

            if (sortedDates.length > 0) {
                const firstDate = new Date(sortedDates[0]);
                firstDate.setDate(firstDate.getDate() - 1);
                rows.push({
                    date: firstDate.toISOString().split('T')[0],
                    'Whole Baseline': 0,
                    'Live Grid Filtered': 0
                });
            }

            sortedDates.forEach(date => {
                rWhole += (wholeDailyPnL[date] || 0);
                rFiltered += (filteredDailyPnL[date] || 0);
                rows.push({
                    date,
                    'Whole Baseline': Math.round(rWhole * 100) / 100,
                    'Live Grid Filtered': Math.round(rFiltered * 100) / 100
                });
            });

            return {
                id: panel.id,
                strategy: targetStrategy,
                data: rows,
                wholeTradeCount,
                filteredTradeCount,
                wholeEndEquity: rWhole,
                filteredEndEquity: rFiltered,
                gridAlpha: rFiltered - rWhole
            };
        });
    }, [cumulativeSubMode, multiGridCount, multiPanels, equityTrades, groupingMode, pnlUnit, baselineScope, filterOnBaseline, activeRegimes, equityChartData.seriesList]);

    const activeSeriesMetrics = useMemo(() => {
        const metrics = {};
        const groupedTrades = {};
        
        equityTrades.forEach(trade => {
            let key = '';
            if (groupingMode === 'strategy') {
                key = trade.strategy || 'Unknown';
            } else if (groupingMode === 'regime') {
                key = trade.regime_code || 'Unknown';
            } else if (groupingMode === 'asset') {
                key = trade.index_name || 'Unknown';
            } else if (groupingMode === 'asset_strategy') {
                key = (trade.index_name || 'Unknown') + ' - ' + (trade.strategy || 'Unknown');
            } else if (groupingMode === 'asset_regime') {
                key = (trade.index_name || 'Unknown') + ' - ' + (trade.regime_code || 'Unknown');
            } else if (groupingMode === 'asset_strategy_regime') {
                key = (trade.index_name || 'Unknown') + ' - ' + (trade.strategy || 'Unknown') + ' - ' + (trade.regime_code || 'Unknown');
            } else {
                key = 'Portfolio';
            }

            if (!groupedTrades[key]) groupedTrades[key] = [];
            groupedTrades[key].push(trade);
        });

        Object.entries(groupedTrades).forEach(([series, trades]) => {
            const sortedTrades = [...trades].sort((a, b) => new Date(a.sell_date || a.buy_date) - new Date(b.sell_date || b.buy_date));
            
            let totalPnL = 0;
            let wins = 0;
            let losses = 0;
            let winSum = 0;
            let lossSum = 0;
            let peak = 0;
            let maxDD = 0;
            let runningEquity = 0;

            sortedTrades.forEach(t => {
                const lotSize = getLotSize(t.index_name, t.ticker);
                const pnl = pnlUnit === 'inr' ? Number(t.pnl || 0) * lotSize : Number(t.pnl_pct || 0) * 100;
                totalPnL += pnl;
                runningEquity += pnl;

                if (pnl > 0) {
                    wins++;
                    winSum += pnl;
                } else if (pnl < 0) {
                    losses++;
                    lossSum += Math.abs(pnl);
                }

                if (runningEquity > peak) peak = runningEquity;
                const dd = peak - runningEquity;
                if (dd > maxDD) maxDD = dd;
            });

            const winRate = trades.length > 0 ? (wins / trades.length) * 100 : 0;
            const profitFactor = lossSum > 0 ? winSum / lossSum : winSum > 0 ? Infinity : 1;

            metrics[series] = {
                totalPnL,
                tradeCount: trades.length,
                winRate,
                profitFactor,
                maxDD
            };
        });

        return metrics;
    }, [equityTrades, groupingMode, pnlUnit]);



    const liveGridCount = useMemo(() => {
        return equityChartData.seriesList.filter(s => checkIsLiveGrid(s)).length;
    }, [equityChartData.seriesList, checkIsLiveGrid]);

    const sortedSeriesList = useMemo(() => {
        const list = [...equityChartData.seriesList];
        if (sidebarSort === 'pnl') {
            return list.sort((a, b) => {
                const pnlA = activeSeriesMetrics[a]?.totalPnL || 0;
                const pnlB = activeSeriesMetrics[b]?.totalPnL || 0;
                return pnlB - pnlA; // High to low
            });
        }
        return list; // Already sorted alphabetically
    }, [equityChartData.seriesList, activeSeriesMetrics, sidebarSort]);

    // Checkbox auto-selection: fires when user explicitly changes sidebar filter preset mode or grouping mode
    useEffect(() => {
        const hasActiveRegimesData = Object.keys(activeRegimes).length > 0;
        if (!hasActiveRegimesData) return;

        if (sidebarFilterMode === 'live' || sidebarFilterMode === 'active_regimes') {
            const filteredSet = new Set(
                equityChartData.seriesList.filter(seriesName => {
                    const getSeriesRegime = () => {
                        if (groupingMode === 'regime') return seriesName;
                        if (groupingMode === 'asset_regime') return seriesName.split(' - ')[1] || '';
                        if (groupingMode === 'asset_strategy_regime') return seriesName.split(' - ')[2] || '';
                        return '';
                    };
                    const getSeriesStrategy = () => {
                        if (groupingMode === 'strategy') return seriesName;
                        if (groupingMode === 'asset_strategy') return seriesName.split(' - ')[1] || '';
                        if (groupingMode === 'asset_strategy_regime') return seriesName.split(' - ')[1] || '';
                        return '';
                    };
                    const strategy = getSeriesStrategy();
                    const regime = getSeriesRegime();

                    const isStratActiveInGrid = (stratName) => {
                        const config = activeRegimes[stratName];
                        if (!config) return false;
                        if (selectedOptionType === 'All') return (config['CE']?.length > 0 || config['PE']?.length > 0);
                        return (config[selectedOptionType.toUpperCase()]?.length > 0);
                    };
                    const isRegimeActiveInGrid = (regName) => {
                        const regUpper = regName.toUpperCase();
                        return Object.values(activeRegimes).some(config =>
                            (config['CE'] || []).includes(regUpper) || (config['PE'] || []).includes(regUpper)
                        );
                    };
                    const isComboLiveInGrid = (stratName, regName) => {
                        const config = activeRegimes[stratName];
                        if (!config) return false;
                        const regUpper = regName.toUpperCase();
                        if (selectedOptionType === 'All') return (config['CE'] || []).includes(regUpper) || (config['PE'] || []).includes(regUpper);
                        return (config[selectedOptionType.toUpperCase()] || []).includes(regUpper);
                    };

                    if (sidebarFilterMode === 'live') {
                        if (groupingMode === 'strategy' || groupingMode === 'asset_strategy') return isStratActiveInGrid(strategy);
                        if (groupingMode === 'regime' || groupingMode === 'asset_regime') return isRegimeActiveInGrid(regime);
                        if (groupingMode === 'asset_strategy_regime') return isComboLiveInGrid(strategy, regime);
                        return false;
                    }
                    if (sidebarFilterMode === 'active_regimes') {
                        if (groupingMode === 'regime' || groupingMode === 'asset_regime') return isRegimeActiveInGrid(regime);
                        if (groupingMode === 'strategy' || groupingMode === 'asset_strategy') return isStratActiveInGrid(strategy);
                        if (groupingMode === 'asset_strategy_regime') return isRegimeActiveInGrid(regime);
                        return false;
                    }
                    return false;
                })
            );

            const newSelection = {};
            equityChartData.seriesList.forEach(s => {
                newSelection[s] = filteredSet.has(s);
            });
            setSelectedSeries(newSelection);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sidebarFilterMode, activeRegimes, groupingMode, selectedOptionType]);

    // Preserve existing user checkbox selection or set default top-5 on initial load
    useEffect(() => {
        if (sidebarFilterMode !== 'all') return;
        if (equityChartData.seriesList.length === 0) return;

        setSelectedSeries(prevSelection => {
            const hasExistingKeys = Object.keys(prevSelection).length > 0;
            if (hasExistingKeys) {
                const preserved = {};
                equityChartData.seriesList.forEach(s => {
                    preserved[s] = prevSelection[s] !== undefined ? prevSelection[s] : true;
                });
                return preserved;
            }

            const list = equityChartData.seriesList;
            const seriesTotals = {};
            list.forEach(s => { seriesTotals[s] = 0; });
            equityTrades.forEach(trade => {
                let key = '';
                if (groupingMode === 'strategy') key = trade.strategy || 'Unknown';
                else if (groupingMode === 'regime') key = trade.regime_code || 'Unknown';
                else if (groupingMode === 'asset') key = trade.index_name || 'Unknown';
                else if (groupingMode === 'asset_strategy') key = (trade.index_name || 'Unknown') + ' - ' + (trade.strategy || 'Unknown');
                else if (groupingMode === 'asset_regime') key = (trade.index_name || 'Unknown') + ' - ' + (trade.regime_code || 'Unknown');
                else if (groupingMode === 'asset_strategy_regime') key = (trade.index_name || 'Unknown') + ' - ' + (trade.strategy || 'Unknown') + ' - ' + (trade.regime_code || 'Unknown');
                else key = 'Portfolio';
                if (seriesTotals[key] !== undefined) {
                    const lotSize = getLotSize(trade.index_name, trade.ticker);
                    const pnlVal = pnlUnit === 'inr' ? Number(trade.pnl || 0) * lotSize : Number(trade.pnl_pct || 0) * 100;
                    seriesTotals[key] += pnlVal;
                }
            });
            const sortedByPnL = [...list].sort((a, b) => seriesTotals[b] - seriesTotals[a]);
            const limit = list.length <= 8 ? list.length : 5;
            const newSelection = {};
            list.forEach(s => { newSelection[s] = sortedByPnL.slice(0, limit).includes(s); });
            return newSelection;
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [groupingMode]);

    const visiblePortfolioMetrics = useMemo(() => {
        const checkedTrades = equityTrades.filter(trade => {
            let key = '';
            if (groupingMode === 'strategy') {
                key = trade.strategy || 'Unknown';
            } else if (groupingMode === 'regime') {
                key = trade.regime_code || 'Unknown';
            } else if (groupingMode === 'asset') {
                key = trade.index_name || 'Unknown';
            } else if (groupingMode === 'asset_strategy') {
                key = (trade.index_name || 'Unknown') + ' - ' + (trade.strategy || 'Unknown');
            } else if (groupingMode === 'asset_regime') {
                key = (trade.index_name || 'Unknown') + ' - ' + (trade.regime_code || 'Unknown');
            } else if (groupingMode === 'asset_strategy_regime') {
                key = (trade.index_name || 'Unknown') + ' - ' + (trade.strategy || 'Unknown') + ' - ' + (trade.regime_code || 'Unknown');
            } else {
                key = 'Portfolio';
            }
            return selectedSeries[key];
        });

        if (checkedTrades.length === 0) {
            return { totalPnL: 0, tradeCount: 0, winRate: 0, profitFactor: 0, maxDD: 0 };
        }

        const sorted = [...checkedTrades].sort((a, b) => new Date(a.sell_date || a.buy_date) - new Date(b.sell_date || b.buy_date));
        
        let totalPnL = 0;
        let wins = 0;
        let losses = 0;
        let winSum = 0;
        let lossSum = 0;
        let peak = 0;
        let maxDD = 0;
        let runningEquity = 0;

        sorted.forEach(t => {
            const lotSize = getLotSize(t.index_name, t.ticker);
            const pnl = pnlUnit === 'inr' ? Number(t.pnl || 0) * lotSize : Number(t.pnl_pct || 0) * 100;
            totalPnL += pnl;
            runningEquity += pnl;

            if (pnl > 0) {
                wins++;
                winSum += pnl;
            } else if (pnl < 0) {
                losses++;
                lossSum += Math.abs(pnl);
            }

            if (runningEquity > peak) peak = runningEquity;
            const dd = peak - runningEquity;
            if (dd > maxDD) maxDD = dd;
        });

        return {
            totalPnL,
            tradeCount: checkedTrades.length,
            winRate: (wins / checkedTrades.length) * 100,
            profitFactor: lossSum > 0 ? winSum / lossSum : winSum > 0 ? Infinity : 1,
            maxDD
        };
    }, [equityTrades, groupingMode, selectedSeries, pnlUnit]);

    const handleCellClick = async (strategy, regime, type) => {
        setSelectedCell({ strategy, regime, type });
        setTrades([]);
        setTradesLoading(true);
        setSelectedCell({ strategy, regime, type });
        setTradesLoading(true);
        setTradesError(null);
        try {
            if (type === 'live') {
                const url = server.godseye.baseUrl + 'audit-results?start_date=' + encodeURIComponent(startDate) + '&end_date=' + encodeURIComponent(endDate) + '&limit=10000&mode=adaptive_live';
                const res = await apiClient.get(url);
                const filtered = res.data.filter(row => {
                    if (row.strategy_tag_l !== strategy) return false;
                    if (regime !== 'All') {
                        const rowRegime = row.regime || '';
                        if (rowRegime.toUpperCase() !== regime.toUpperCase()) return false;
                    }
                    const tradeDate = row.buy_time_l ? row.buy_time_l.split('T')[0] : null;
                    if (!tradeDate || tradeDate < startDate || tradeDate > endDate) return false;
                    return row.symbol_l || row.buy_price_l !== null;
                });
                setTrades(filtered);
            } else {
                const params = new URLSearchParams({
                    start_date: startDate,
                    end_date: endDate,
                    strategy,
                    limit: 10000,
                });
                if (selectedIndex && selectedIndex.toUpperCase() !== 'ALL') {
                    params.append('index_name', selectedIndex);
                }
                if (selectedOptionType.toLowerCase() !== 'all') {
                    params.append('ce_pe', selectedOptionType.toUpperCase());
                }
                if (regime !== 'All') params.append('regime_code', regime);
                const url = server.godseye.baseUrl + 'audit-results/raw-backtest?' + params.toString();
                const res = await apiClient.get(url);
                setTrades(res.data);
            }
        } catch (e) {
            console.error("Failed to fetch trades for adaptive dashboard cell", e);
            setTradesError("Failed to load trades.");
        } finally {
            setTradesLoading(false);
        }
    };

    const METRIC_DEFINITIONS = [
        { label: 'Trade Count',       key: 'trade_count' },
        { label: 'Accuracy',          key: 'accuracy' },
        { label: 'Avg Win',           key: 'avg_win' },
        { label: 'Avg Loss',          key: 'avg_loss' },
        { label: 'Win/Loss Ratio',    key: 'win_loss_ratio' },
        { label: 'Expectancy',        key: 'expectancy' },
        { label: 'Impact',            key: 'impact' },
        { label: 'Sharpe',            key: 'sharpe' },
        { label: 'PnL/DD Ratio',      key: 'pnl_dd_ratio' },
        { label: 'Max Drawdown (₹)',  key: 'max_drawdown' },
        { label: 'Median Win',        key: 'median_win' },
        { label: 'Median Loss',       key: 'median_loss' },
        { label: 'Median Overall',    key: 'median_overall' },
        { label: 'Max Consec Losses', key: 'max_consecutive_losses' },
    ];

    const REGIME_COLORS = {
        All: 'text-blue-300',
        F00: 'text-cyan-400', F01: 'text-cyan-300', F10: 'text-cyan-400', F11: 'text-cyan-300',
        S00: 'text-amber-400', S01: 'text-amber-300', S10: 'text-amber-400', S11: 'text-amber-300',
        R00: 'text-red-400',  R01: 'text-red-300',  R10: 'text-red-400',  R11: 'text-red-300',
    };

    const cellColor = (label, value) => {
        if (value === '-' || value === undefined || value === null || value === 0) return 'text-gray-500';
        const valStr = String(value);
        if (valStr === '0' || valStr === '0.00' || valStr === '0.00%') return 'text-gray-500';
        const lowerLabel = label.toLowerCase();
        const num = parseFloat(valStr.replace(/[^d.-]/g, ''));
        if (isNaN(num)) return 'text-gray-300';
        if (lowerLabel.includes('loss') || lowerLabel.includes('drawdown')) {
            return num < 0 ? 'text-red-400 font-semibold' : 'text-emerald-400 font-semibold';
        }
        return num > 0 ? 'text-emerald-400 font-semibold' : num < 0 ? 'text-red-400 font-semibold' : 'text-gray-400';
    };

    const regimes = stats?.regimes || [];
    const strategies = Object.keys(stats?.strategies || {});

    return (
        <div className="flex flex-col gap-4 h-full">
            {/* Ultra-Compact Control Bar */}
            <div className="flex flex-wrap items-center justify-between gap-3 bg-[#1a1d29] px-3.5 py-2 rounded-xl border border-gray-800 shrink-0">
                <div className="flex flex-wrap items-center gap-3">
                    {/* Index Selector Bar */}
                    <div className="flex bg-[#14161f] p-0.5 rounded-lg border border-gray-700/80 gap-0.5">
                        {['ALL', 'NIFTY', 'BANKNIFTY', 'FINNIFTY', 'SENSEX', 'MIDCPNIFTY'].map((idxName) => (
                            <button
                                key={idxName}
                                onClick={() => setSelectedIndex(idxName)}
                                className={`px-2.5 py-1 rounded text-[11px] font-bold transition-all cursor-pointer ${
                                    selectedIndex === idxName 
                                        ? 'bg-blue-600 text-white shadow' 
                                        : 'text-gray-400 hover:text-gray-200'
                                }`}
                            >
                                {idxName === 'ALL' ? 'ALL ASSETS' : (idxName === 'MIDCPNIFTY' ? 'MIDCAPNIFTY' : idxName)}
                            </button>
                        ))}
                    </div>

                    <div className="h-4 w-px bg-gray-800" />

                    {/* Date Range Inputs */}
                    <div className="flex items-center gap-1.5 text-[11px] font-bold text-gray-400">
                        <span className="uppercase text-[10px] tracking-wider text-gray-500">Date Range</span>
                        <input
                            type="date" value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            className="bg-[#14161f] border border-gray-700 rounded px-2 py-0.5 text-[11px] text-gray-200 font-mono outline-none focus:border-blue-500"
                            style={{ colorScheme: 'dark' }}
                        />
                        <span className="text-gray-600">to</span>
                        <input
                            type="date" value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            className="bg-[#14161f] border border-gray-700 rounded px-2 py-0.5 text-[11px] text-gray-200 font-mono outline-none focus:border-blue-500"
                            style={{ colorScheme: 'dark' }}
                        />

                        {/* Quick Year Preset Pills */}
                        <div className="flex bg-[#14161f] p-0.5 rounded border border-gray-700/80 gap-0.5 ml-1">
                            {[
                                { label: '1Y', yrs: 1 },
                                { label: '3Y', yrs: 3 },
                                { label: '5Y', yrs: 5 },
                                { label: '6Y', yrs: 6 },
                                { label: 'MAX', yrs: 'MAX' }
                            ].map(p => (
                                <button
                                    key={p.label}
                                    onClick={() => applyYearPreset(p.yrs)}
                                    className="px-1.5 py-0.5 rounded text-[10px] font-bold text-gray-400 hover:text-white hover:bg-blue-600/30 transition-all cursor-pointer"
                                    title={`Set start date to ${p.yrs === 'MAX' ? '2021-01-01' : p.yrs + ' year(s) prior to end date'}`}
                                >
                                    {p.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="h-4 w-px bg-gray-800" />

                    {/* Option Type Selector */}
                    <div className="flex items-center gap-1.5">
                        <span className="text-[10px] text-gray-500 font-bold uppercase tracking-wider">Option</span>
                        <div className="flex bg-[#14161f] p-0.5 rounded border border-gray-700 gap-0.5">
                            {['All', 'CE', 'PE'].map((optType) => (
                                <button
                                    key={optType}
                                    onClick={() => {
                                        setSelectedOptionType(optType);
                                        if (optType === 'CE') setSelectedCEPE(['CE']);
                                        else if (optType === 'PE') setSelectedCEPE(['PE']);
                                        else setSelectedCEPE(['CE', 'PE']);
                                    }}
                                    className={`px-2.5 py-0.5 rounded text-[11px] font-bold transition-all cursor-pointer ${
                                        selectedOptionType === optType 
                                            ? 'bg-blue-600 text-white shadow' 
                                            : 'text-gray-400 hover:text-gray-200'
                                    }`}
                                >
                                    {optType}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="flex items-center gap-2">
                    {/* Sub Tab Selector */}
                    <div className="flex bg-[#14161f] p-0.5 rounded-lg border border-gray-700/80 gap-0.5">
                        <button
                            onClick={() => setDashboardSubTab('matrix')}
                            className={`px-3 py-1 rounded text-[11px] font-bold transition-all cursor-pointer ${
                                dashboardSubTab === 'matrix' 
                                    ? 'bg-blue-600 text-white shadow' 
                                    : 'text-gray-400 hover:text-gray-200'
                            }`}
                        >
                            Performance Matrix
                        </button>
                        <button
                            onClick={() => setDashboardSubTab('equity_curve')}
                            className={`px-3 py-1 rounded text-[11px] font-bold transition-all cursor-pointer ${
                                dashboardSubTab === 'equity_curve' 
                                    ? 'bg-blue-600 text-white shadow' 
                                    : 'text-gray-400 hover:text-gray-200'
                            }`}
                        >
                            Equity Curve Explorer
                        </button>
                    </div>

                    <button
                        onClick={fetchRankings}
                        className="px-3 py-1 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 rounded-lg text-[11px] font-bold flex items-center gap-1.5 transition-all shadow-sm cursor-pointer"
                    >
                        <TrendingUp className="w-3.5 h-3.5" /> Rankings
                    </button>
                    <button
                        onClick={() => {
                            fetchStats();
                            if (dashboardSubTab === 'equity_curve') {
                                fetchEquityTrades();
                            }
                        }}
                        className="px-3 py-1 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-[11px] font-bold flex items-center gap-1.5 transition-all shadow cursor-pointer"
                    >
                        <RefreshCw className={`w-3.5 h-3.5 ${loading || (dashboardSubTab === 'equity_curve' && equityLoading) ? 'animate-spin' : ''}`} /> Refresh
                    </button>
                </div>
            </div>

            {/* Content Area */}
            {dashboardSubTab === 'matrix' ? (
                /* Matrix Performance Grid */
                <div className="bg-[#1a1d29] border border-gray-800 rounded-xl overflow-hidden flex-1 flex flex-col">
                    {error && (
                        <div className="bg-red-500/10 border-b border-red-500/30 p-3 text-sm text-red-400 flex items-center gap-2 shrink-0">
                            <AlertCircle className="w-4 h-4" /> {error}
                        </div>
                    )}

                    <div className="flex-1 overflow-auto custom-scrollbar">
                        {loading ? (
                            <div className="flex flex-col items-center justify-center h-64 gap-2 text-gray-400">
                                <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
                                <span>Loading metrics...</span>
                            </div>
                        ) : !stats || strategies.length === 0 ? (
                            <div className="text-center py-20 text-gray-500">
                                No backtest audit results found for the selected date range.
                            </div>
                        ) : (
                            <table className="text-left border-collapse text-xs whitespace-nowrap border-b border-gray-800/40">
                                <thead className="bg-[#14161f] sticky top-0 z-10">
                                    <tr>
                                        <th className="sticky left-0 z-20 bg-[#14161f] px-4 py-3 border-b border-r border-[#1F2937] text-gray-400 font-bold uppercase min-w-[140px]">Strategy</th>
                                        <th className="sticky left-[140px] z-20 bg-[#14161f] px-4 py-3 border-b border-r border-[#1F2937] text-gray-400 font-bold uppercase min-w-[160px]">Metric</th>
                                        {regimes.map((regime) => (
                                            <th
                                                key={regime}
                                                className={`px-4 py-3 border-b border-r border-[#1F2937] text-center font-bold uppercase min-w-[120px] ${REGIME_COLORS[regime] || 'text-blue-300'}`}
                                            >
                                                {regime}
                                            </th>
                                        ))}
                                    </tr>
                                    <tr className="text-gray-500 uppercase text-[10px]">
                                        <th className="sticky left-0 z-20 bg-[#0f1116] px-4 py-2 border-b border-r border-[#1F2937] min-w-[140px]"></th>
                                        <th className="sticky left-[140px] z-20 bg-[#0f1116] px-4 py-2 border-b border-r border-[#1F2937] min-w-[160px]"></th>
                                        {regimes.map((regime) => (
                                            <th key={regime} className="px-3 py-2 border-b border-r border-[#1F2937] bg-[#0f1116] text-center min-w-[120px]">
                                                Historical
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-800/30 text-gray-300">
                                    {strategies.map((stratName) => {
                                        const stratData = stats.strategies[stratName];
                                        return (
                                            <React.Fragment key={stratName}>
                                                {METRIC_DEFINITIONS.map((metric, idx) => (
                                                    <tr key={metric.key} className={`hover:bg-gray-800/20 transition-colors ${idx === METRIC_DEFINITIONS.length - 1 ? 'border-b border-gray-700' : ''}`}>
                                                        {idx === 0 ? (
                                                            <td
                                                                rowSpan={METRIC_DEFINITIONS.length}
                                                                className="sticky left-0 z-10 bg-[#1a1d29] px-4 py-3 font-bold border-r border-gray-800/80 align-top min-w-[140px]"
                                                            >
                                                                <div className="sticky top-20">
                                                                    <span className="text-sm text-blue-400">{stratName}</span>
                                                                </div>
                                                            </td>
                                                        ) : null}
                                                        <td className="sticky left-[140px] z-10 bg-[#1a1d29] px-4 py-2 text-gray-400 border-r border-gray-800/50 font-medium min-w-[160px]">
                                                            {metric.label}
                                                        </td>
                                                        {regimes.map((regime) => {
                                                            const regimeData = stratData[regime] || {};
                                                            const histVal = regimeData.historical?.[metric.key];
                                                            const hasHistTrades = (regimeData.historical?.trade_count || 0) > 0;

                                                            const isCellActive = (() => {
                                                                if (regime === 'All') return false;
                                                                const stratActive = activeRegimes[stratName];
                                                                if (!stratActive) return false;
                                                                const regUpper = regime.toUpperCase();
                                                                if (selectedOptionType === 'All') {
                                                                    const ceActive = stratActive['CE'] || [];
                                                                    const peActive = stratActive['PE'] || [];
                                                                    return ceActive.includes(regUpper) || peActive.includes(regUpper);
                                                                } else {
                                                                    const typeActive = stratActive[selectedOptionType.toUpperCase()] || [];
                                                                    return typeActive.includes(regUpper);
                                                                }
                                                            })();

                                                            const highlightClasses = isCellActive
                                                                ? 'bg-emerald-500/10 shadow-[inset_0_0_4px_rgba(16,185,129,0.1)] border-x border-emerald-500/30 font-bold'
                                                                : '';

                                                            return (
                                                                <td 
                                                                    key={regime}
                                                                    className={`px-3 py-2 text-center border-r border-gray-800/50 font-mono transition-colors duration-150 ${cellColor(metric.label, histVal)} ${highlightClasses} ${hasHistTrades ? 'cursor-pointer hover:bg-blue-500/10 hover:text-white' : ''}`}
                                                                    onClick={() => hasHistTrades && handleCellClick(stratName, regime, 'historical')}
                                                                >
                                                                    {histVal ?? '-'}
                                                                </td>
                                                            );
                                                        })}
                                                    </tr>
                                                ))}
                                            </React.Fragment>
                                        );
                                    })}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>


            ) : (
                /* Equity Curve Explorer sub-tab */
                <div className="flex flex-col flex-1 min-h-0">
                    {equityLoading ? (
                        <div className="flex flex-col items-center justify-center flex-1 bg-[#1a1d29] border border-gray-800 rounded-xl min-h-[400px] gap-2 text-gray-400">
                            <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
                            <span>Computing equity curve statistics...</span>
                        </div>
                    ) : equityError ? (
                        <div className="flex flex-col items-center justify-center flex-1 bg-[#1a1d29] border border-gray-800 rounded-xl min-h-[400px] text-red-400 gap-2">
                            <AlertCircle className="w-8 h-8" />
                            <span>{equityError}</span>
                        </div>
                    ) : equityTrades.length === 0 ? (
                        <div className="flex flex-col items-center justify-center flex-1 bg-[#1a1d29] border border-gray-800 rounded-xl min-h-[400px] text-gray-500">
                            <AlertCircle className="w-8 h-8 mb-2 opacity-50 text-orange-400" />
                            <span>No backtest trades found for the active filter set.</span>
                        </div>
                    ) : (
                        <div className="flex flex-col flex-1 min-h-0 gap-4">
                            {/* Summary KPI Cards */}
                            {chartView === 'cumulative' ? (
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 shrink-0">
                                    <div className="bg-[#1a1d29] border border-gray-800 px-3 py-1.5 rounded-lg flex items-center justify-between shadow-sm">
                                        <span className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Trades Analyzed</span>
                                        <div className="flex items-baseline gap-2 font-mono">
                                            <span className="text-xs font-bold text-gray-100">{(equityChartData.wholeTradeCount || 0).toLocaleString()} Whole</span>
                                            <span className="text-[11px] font-bold text-emerald-400">{(equityChartData.filteredTradeCount || 0).toLocaleString()} Live</span>
                                        </div>
                                    </div>

                                    <div className="bg-[#1a1d29] border border-gray-800 px-3 py-1.5 rounded-lg flex items-center justify-between shadow-sm">
                                        <span className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Whole Tradelist PnL</span>
                                        <span className="text-xs font-bold text-gray-200 font-mono">
                                            {pnlUnit === 'inr'
                                                ? '₹' + Math.round(equityChartData.wholeEndEquity || 0).toLocaleString('en-IN')
                                                : (equityChartData.wholeEndEquity >= 0 ? '+' : '') + (equityChartData.wholeEndEquity || 0).toFixed(2) + '%'}
                                        </span>
                                    </div>

                                    <div className="bg-[#1a1d29] border border-emerald-500/30 px-3 py-1.5 rounded-lg bg-emerald-500/5 flex items-center justify-between shadow-sm">
                                        <span className="text-[10px] text-emerald-400 font-bold uppercase tracking-wider">Live Grid Filtered PnL</span>
                                        <span className="text-xs font-bold text-emerald-400 font-mono">
                                            {pnlUnit === 'inr'
                                                ? '₹' + Math.round(equityChartData.filteredEndEquity || 0).toLocaleString('en-IN')
                                                : (equityChartData.filteredEndEquity >= 0 ? '+' : '') + (equityChartData.filteredEndEquity || 0).toFixed(2) + '%'}
                                        </span>
                                    </div>

                                    <div className="bg-[#1a1d29] border border-amber-500/30 px-3 py-1.5 rounded-lg bg-amber-500/5 flex items-center justify-between shadow-sm">
                                        <span className="text-[10px] text-amber-400 font-bold uppercase tracking-wider">Grid Alpha Advantage</span>
                                        <span className={`text-xs font-bold font-mono ${equityChartData.gridAlpha >= 0 ? 'text-amber-400' : 'text-rose-400'}`}>
                                            {pnlUnit === 'inr'
                                                ? (equityChartData.gridAlpha >= 0 ? '+' : '') + '₹' + Math.round(equityChartData.gridAlpha || 0).toLocaleString('en-IN')
                                                : (equityChartData.gridAlpha >= 0 ? '+' : '') + (equityChartData.gridAlpha || 0).toFixed(2) + '%'}
                                        </span>
                                    </div>
                                </div>
                            ) : (
                                <div className="grid grid-cols-2 md:grid-cols-5 gap-2 shrink-0">
                                    <div className="bg-[#1a1d29] border border-gray-800 px-3 py-1.5 rounded-lg flex items-center justify-between">
                                        <span className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Visible PnL</span>
                                        <span className={`text-xs font-bold font-mono ${visiblePortfolioMetrics.totalPnL >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                                            {pnlUnit === 'inr' 
                                                ? '₹' + Math.round(visiblePortfolioMetrics.totalPnL).toLocaleString('en-IN')
                                                : (visiblePortfolioMetrics.totalPnL >= 0 ? '+' : '') + visiblePortfolioMetrics.totalPnL.toFixed(2) + '%'}
                                        </span>
                                    </div>
                                    <div className="bg-[#1a1d29] border border-gray-800 px-3 py-1.5 rounded-lg flex items-center justify-between">
                                        <span className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Trades</span>
                                        <span className="text-xs font-bold text-gray-200 font-mono">
                                            {visiblePortfolioMetrics.tradeCount}
                                        </span>
                                    </div>
                                    <div className="bg-[#1a1d29] border border-gray-800 px-3 py-1.5 rounded-lg flex items-center justify-between">
                                        <span className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Win Rate</span>
                                        <span className="text-xs font-bold text-blue-400 font-mono">
                                            {visiblePortfolioMetrics.winRate.toFixed(1)}%
                                        </span>
                                    </div>
                                    <div className="bg-[#1a1d29] border border-gray-800 px-3 py-1.5 rounded-lg flex items-center justify-between">
                                        <span className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Profit Factor</span>
                                        <span className="text-xs font-bold text-teal-400 font-mono">
                                            {visiblePortfolioMetrics.profitFactor === Infinity ? '∞' : visiblePortfolioMetrics.profitFactor.toFixed(2)}
                                        </span>
                                    </div>
                                    <div className="bg-[#1a1d29] border border-gray-800 px-3 py-1.5 rounded-lg flex items-center justify-between col-span-2 md:col-span-1">
                                        <span className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Max DD</span>
                                        <span className="text-xs font-bold text-rose-500 font-mono">
                                            {pnlUnit === 'inr' 
                                                ? '₹' + Math.round(visiblePortfolioMetrics.maxDD).toLocaleString('en-IN')
                                                : visiblePortfolioMetrics.maxDD.toFixed(2) + '%'}
                                        </span>
                                    </div>
                                </div>
                            )}

                            {/* Chart + Sidebar container */}
                            <div className="flex flex-col lg:flex-row gap-4 flex-1 min-h-0">
                                {/* Chart Main Box */}
                                <div className="bg-[#1a1d29] border border-gray-800 rounded-xl p-4 flex-1 flex flex-col min-h-0">
                                    <BenchmarkToolbar
                                        chartView={chartView}
                                        setChartView={setChartView}
                                        cumulativeSubMode={cumulativeSubMode}
                                        setCumulativeSubMode={setCumulativeSubMode}
                                        multiGridCount={multiGridCount}
                                        setMultiGridCount={setMultiGridCount}
                                        setIsMultiGridFullscreen={setIsMultiGridFullscreen}
                                        showWholeBaseline={showWholeBaseline}
                                        setShowWholeBaseline={setShowWholeBaseline}
                                        showLiveGridFiltered={showLiveGridFiltered}
                                        setShowLiveGridFiltered={setShowLiveGridFiltered}
                                        baselineScope={baselineScope}
                                        setBaselineScope={setBaselineScope}
                                        filterOnBaseline={filterOnBaseline}
                                        setFilterOnBaseline={setFilterOnBaseline}
                                        highlightLive={highlightLive}
                                        setHighlightLive={setHighlightLive}
                                        pnlUnit={pnlUnit}
                                        setPnlUnit={setPnlUnit}
                                        groupingMode={groupingMode}
                                        setGroupingMode={setGroupingMode}
                                        selectedFSR={selectedFSR}
                                        setSelectedFSR={setSelectedFSR}
                                        selectedMABits={selectedMABits}
                                        setSelectedMABits={setSelectedMABits}
                                        selectedCEPE={selectedCEPE}
                                        setSelectedCEPE={setSelectedCEPE}
                                        equityChartData={equityChartData}
                                        selectedSeries={selectedSeries}
                                        multiPanels={multiPanels}
                                        setMultiPanels={setMultiPanels}
                                        pinnedSeries={pinnedSeries}
                                        setPinnedSeries={setPinnedSeries}
                                    />

                                    <EquityChartContainer
                                        chartView={chartView}
                                        cumulativeSubMode={cumulativeSubMode}
                                        multiGridCount={multiGridCount}
                                        multiGridChartData={multiGridChartData}
                                        multiPanels={multiPanels}
                                        setMultiPanels={setMultiPanels}
                                        equityChartData={equityChartData}
                                        pnlUnit={pnlUnit}
                                        selectedSeries={selectedSeries}
                                        pinnedSeries={pinnedSeries}
                                        setPinnedSeries={setPinnedSeries}
                                        hoveredSeries={hoveredSeries}
                                        setHoveredSeries={setHoveredSeries}
                                        checkIsLiveGrid={checkIsLiveGrid}
                                        sortedSeriesList={sortedSeriesList}
                                        highlightLive={highlightLive}
                                        showWholeBaseline={showWholeBaseline}
                                        showLiveGridFiltered={showLiveGridFiltered}
                                    />
                                </div>

                                {/* Sidebar Series Checklist */}
                                <SidebarSeriesChecklist
                                    equityChartData={equityChartData}
                                    selectedSeries={selectedSeries}
                                    setSelectedSeries={setSelectedSeries}
                                    liveGridCount={liveGridCount}
                                    sidebarSort={sidebarSort}
                                    setSidebarSort={setSidebarSort}
                                    sidebarFilterMode={sidebarFilterMode}
                                    setSidebarFilterMode={setSidebarFilterMode}
                                    setHighlightLive={setHighlightLive}
                                    sortedSeriesList={sortedSeriesList}
                                    checkIsLiveGrid={checkIsLiveGrid}
                                    hoveredSeries={hoveredSeries}
                                    setHoveredSeries={setHoveredSeries}
                                    pinnedSeries={pinnedSeries}
                                    setPinnedSeries={setPinnedSeries}
                                    activeSeriesMetrics={activeSeriesMetrics}
                                    pnlUnit={pnlUnit}
                                />
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Performance Rankings Modal */}
            <PerformanceRankingsTable
                showRankings={showRankings}
                setShowRankings={setShowRankings}
                startDate={startDate}
                endDate={endDate}
                rankingsTab={rankingsTab}
                setRankingTab={setRankingTab}
                rankingsLoading={rankingsLoading}
                rankingsError={rankingsError}
                rankingsData={rankingsData}
                activeRegimes={activeRegimes}
            />

            {/* Fullscreen Multi-Grid Workspace Overlay Modal */}
            <MultiGridFullscreenModal
                isMultiGridFullscreen={isMultiGridFullscreen}
                setIsMultiGridFullscreen={setIsMultiGridFullscreen}
                multiGridCount={multiGridCount}
                setMultiGridCount={setMultiGridCount}
                pnlUnit={pnlUnit}
                setPnlUnit={setPnlUnit}
                multiGridChartData={multiGridChartData}
                multiPanels={multiPanels}
                setMultiPanels={setMultiPanels}
                equityChartData={equityChartData}
                baselineScope={baselineScope}
                setBaselineScope={setBaselineScope}
                filterOnBaseline={filterOnBaseline}
                setFilterOnBaseline={setFilterOnBaseline}
                setSelectedFSR={setSelectedFSR}
                setSelectedMABits={setSelectedMABits}
                setSelectedCEPE={setSelectedCEPE}
            />
        </div>
    );
};

export default AdaptiveDashboardView;
